import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useScrollTransition } from 'util/react-transition-component';
import { renderCta } from 'util/cta/cta.template';
import type { CtaRefType } from 'util/cta/cta.data';
import styles from './PlayButton.module.scss';
import {
  mouseEnterAnimation,
  mouseLeaveAnimation,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './PlayButton.transitions';
import type { PlayButtonProps, PlayButtonRef } from './PlayButton.data';
import { defaultButtonType } from './PlayButton.data';

import useMagnetic from '../../../hooks/useMagnetic';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import PlayIcon from '../play-icon/PlayIcon';

export default function PlayButton({
  isVisible,
  disableTriggerInOnScroll = true,
  triggerInOnce = false,
  type = defaultButtonType,
  isMagnetic = true,
  isPlaying = false,
  ref,
  ...props
}: PlayButtonProps & { ref?: Ref<PlayButtonRef> }): ReactElement {
  const localRef = useRef<CtaRefType>(undefined);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const backgroundRef = useRef<HTMLDivElement>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isHidden, setIsHidden] = useState(true);
  const isDesktopDevice = useIsDesktopDevice(false);
  const { isHover } = useMagnetic(
    isMagnetic && isDesktopDevice,
    {
      element: elementRef,
      scaleElement: backgroundRef,
      innerElement: labelRef,
    },
    {
      strength: 0.5,
    },
    !isHidden,
  );

  const { transitionController } = useScrollTransition({
    scroll: {
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: {
        once: triggerInOnce,
      },
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        labelRef,
      },
      onComplete(direction) {
        setIsHidden(direction === 'out');
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);

  const onMouseLeave = useCallback(() => {
    if (isDesktopDevice) {
      mouseLeaveAnimation({
        backgroundHoverRef,
        labelRef,
      });
    }
  }, [isDesktopDevice]);

  const onMouseEnter = useCallback(() => {
    if (isDesktopDevice) {
      mouseEnterAnimation({
        backgroundHoverRef,
        labelRef,
      });
    }
  }, [isDesktopDevice]);

  useUpdateEffect(() => {
    if (isHover) {
      onMouseEnter();
    } else {
      onMouseLeave();
    }
  }, [isHover, onMouseEnter, onMouseLeave]);

  return renderCta({
    innerTemplate: (
      <>
        <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')}>
          <div ref={backgroundHoverRef} className={classNames(styles.backgroundHover)} />
        </div>
        <PlayIcon ref={labelRef} size="medium" isPlaying={isPlaying} />
      </>
    ),
    props: {
      type,
      'aria-label': 'play video',
      ...props,
      className: classNames(styles.playButton, props.className, {
        [styles.isHover]: isHover,
      }),
      onMouseEnter: () => {
        if (!isMagnetic) {
          onMouseEnter();
        }
      },
      onMouseLeave: () => {
        if (!isMagnetic) {
          onMouseLeave();
        }
      },
    },
    elementRef,
  });
}
